// Renewal uses the existing public client and login flow. Backend authentication
// remains authoritative; decoding exp here is only for renewal scheduling.
export class SessionExpired extends Error {}
export class SessionUnavailable extends Error {}
export class SessionChanged extends Error {}

const claims = (token) => {
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(decodeURIComponent(Array.from(atob(part),
      (c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join('')));
  } catch (_) { return {}; }
};

export function createSessionManager({ storage, tokenUrl, issuer, clientId,
  environment, now = Date.now, exchange, lock = (_, operation) => operation() }) {
  const key = `fieldops.session.v1.${environment}`;
  let pending;
  const unavailable = () => new SessionUnavailable('Session renewal is temporarily unavailable. Please retry.');
  const changed = () => new SessionChanged('The signed-in session changed.');
  const expired = () => new SessionExpired('Please sign in again.');
  const save = (value) => {
    // The bundle is authoritative and written atomically, including rotated tokens.
    // A null tombstone prevents another tab from importing a legacy token on logout.
    storage.setItem(key, JSON.stringify(value));
    if (value) storage.setItem('token', value.accessToken);
    else storage.removeItem('token');
  };
  const makeTokens = (data, id, previousRefresh) => {
    const payload = claims(data.access_token);
    if (typeof data.access_token !== 'string' || payload.iss !== issuer ||
        typeof payload.sub !== 'string' ||
        ((data.refresh_token || previousRefresh) && payload.azp && payload.azp !== clientId) ||
        typeof payload.exp !== 'number') throw unavailable();
    const expiresAt = Math.min(payload.exp * 1000,
      typeof data.expires_in === 'number' ? now() + data.expires_in * 1000 : Infinity);
    const refreshToken = data.refresh_token || previousRefresh || null;
    return { id, accessToken: data.access_token, refreshToken, expiresAt, subject: payload.sub };
  };
  const read = () => {
    const raw = storage.getItem(key);
    if (raw !== null) {
      try { return JSON.parse(raw); } catch (_) { throw unavailable(); }
    }
    const token = storage.getItem('token');
    if (!token || token === 'null') return null;
    const result = makeTokens({ access_token: token }, `legacy:${token}`);
    save(result);
    return result;
  };
  const clear = (expectedId) => {
    if (expectedId !== undefined && read()?.id !== expectedId) return;
    save(null);
  };
  const install = (data) => {
    const id = `${now()}:${Math.random().toString(36).slice(2)}`;
    const result = makeTokens(data, id);
    if (result.expiresAt <= now()) throw unavailable();
    save(result);
    return result;
  };
  const accessToken = async ({ force = false, rejectedToken, expectedId } = {}) => {
    const initial = read();
    if (expectedId !== undefined && initial?.id !== expectedId) throw changed();
    if (!initial) throw expired();
    const id = initial.id;
    if (rejectedToken && rejectedToken !== initial.accessToken) return initial.accessToken;
    if (!force && initial.expiresAt > now() + 60000) return initial.accessToken;
    if (!initial.refreshToken) {
      if (!force && initial.expiresAt > now()) return initial.accessToken;
      clear(id);
      throw expired();
    }
    if (pending?.id === id) return pending.promise;
    const operation = Promise.resolve().then(() => lock(key, async () => {
      const current = read();
      if (current?.id !== id) throw changed();
      // Another tab may have renewed while this tab waited for the Web Lock.
      if (current.accessToken !== initial.accessToken ||
          (!force && current.expiresAt > now() + 60000)) return current.accessToken;
      try {
        const response = await exchange(tokenUrl, {
          grant_type: 'refresh_token', client_id: clientId, refresh_token: current.refreshToken,
        });
        const latest = read();
        if (latest?.id !== id) throw changed();
        if (latest.accessToken !== current.accessToken) return latest.accessToken;
        if (!response.ok) {
          if ([400, 401].includes(response.status) && response.data?.error === 'invalid_grant') {
            clear(id);
            throw expired();
          }
          throw unavailable();
        }
        const next = makeTokens(response.data, id, current.refreshToken);
        if (next.subject !== current.subject || next.expiresAt <= now()) throw unavailable();
        save(next);
        return next.accessToken;
      } catch (error) {
        if (error instanceof SessionExpired || error instanceof SessionChanged) throw error;
        if (read()?.id !== id) throw changed();
        // A proactive refresh outage doesn't invalidate a still-valid access token.
        if (!force && current.expiresAt > now()) return current.accessToken;
        throw unavailable();
      }
    }));
    pending = { id, promise: operation };
    try { return await operation; }
    finally { if (pending?.promise === operation) pending = undefined; }
  };
  return { read, install, clear, accessToken, key };
}

const issuer = `${process.env.REACT_APP_AUTH_URL?.replace(/\/$/, '')}/realms/${process.env.REACT_APP_Keycloak_Realm}`;
export const session = createSessionManager({
  storage: { getItem: (key) => localStorage.getItem(key), setItem: (key, value) => localStorage.setItem(key, value),
    removeItem: (key) => localStorage.removeItem(key) },
  issuer, tokenUrl: `${issuer}/protocol/openid-connect/token`, clientId: 'fieldops-frontend',
  environment: `${issuer}|fieldops-frontend|${process.env.REACT_APP_API_URL}`,
  lock: (name, operation) => typeof navigator !== 'undefined' && navigator.locks?.request
    ? navigator.locks.request(name, operation) : operation(),
  exchange: async (url, body) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { method: 'POST', signal: controller.signal,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: new URLSearchParams(body) });
      return { ok: response.ok, status: response.status, data: await response.json() };
    } finally { clearTimeout(timer); }
  },
});
